import cx from 'classnames'
import Styles from 'components/WorkspaceDashboard/WorkspaceDashboard.module.scss'
import { UpdateBlazePaymentMethodModal } from 'components/UpdateBlazePaymentMethodModal/UpdateBlazePaymentMethodModal'
import { WorkspaceDashboardSidebar } from 'blaze-components/WorkspaceDashboardSidebar/WorkspaceDashboardSidebar'
import { HandbookProvider } from 'components/Handbook2_0/Sidebar/HandbookProvider'
import { Container } from 'components/WorkspaceDashboard/components/Container/Container'
import { RootState, ViewType, Workspace } from 'types'
import SettingsContainer from 'components/Settings/SettingsContainer'
import { PropsWithChildren, useEffect, useRef } from 'react'
import { useUnregisteredUserBanner } from 'hooks/useUnregisteredUser'
import { useSuperAdmin } from 'hooks/useSuperAdmin'
import { useDispatch, useSelector } from 'react-redux'
import { isEqual } from 'lodash'
import { useRootStore } from 'stores/RootStore'
import { useHandbook } from 'components/Handbook2_0/hooks'
import { useLocation, useRouteMatch } from 'react-router-dom'
import { BLAZE_HOME_PATH, FOLDER_PATH, RECENT_DOCS_PATH } from 'paths'
import { useLongHover } from 'hooks/useLongHover'
import { setFloatingSidebar } from 'redux/actions'
import { useShowFloatingOrgSidebar } from 'hooks/useShowFloatingOrgSidebar'
import { TopBar as NewTopBar } from 'blaze-components/TopBar/TopBar'
import { useSidebarState } from 'blaze-components/WorkspaceDashboardSidebar/hooks/useSidebarState'
import { observer } from 'mobx-react-lite'
import { useLayout } from 'stores/LayoutStore'
import { useAnnualTrialDiscountBanner } from 'hooks/useAnnualTrialDiscountBanner/useAnnualTrialDiscountBanner'
import { useAutopilotPausedBanner } from 'hooks/useAutopilotPausedBanner/useAutopilotPausedBanner'
import { useTrialEndedBanner } from 'hooks/useTrialEndedBanner/useTrialEndedBanner'
import { useAutopilotUpgradeBanner } from 'hooks/useAutopilotUpgradeBanner/useAutopilotUpgradeBanner'
import { useIntegrationMaintenanceBanner } from 'hooks/useIntegrationMaintenanceBanner/useIntegrationMaintenanceBanner'
import { useFreePlanBanner } from 'hooks/useFreePlanBanner/useFreePlanBanner'
import { useIsSystemRedesign } from 'hooks/useIsSystemRedesign'
import { ContentGenerationWatcher } from 'components/GenerationStatus/ContentGenerationWatcher'
import { GenerationStatusNotification } from 'components/GenerationStatus/GenerationStatusNotification'
import { useAPMobileWeb } from 'hooks/useAPMobileWeb'
import { useOnboardingStrategiesEnabled } from 'flows/virtualMarketerOnboarding/steps'
import { MobileStopForStrategies } from 'components/MobileStop/MobileStopForStrategies'
import { useCurrentBrandPlan } from 'hooks/queries/brandPlans/useCurrentBrandPlan'

interface Props {
  workspace: Workspace
}

export const Dashboard = observer(({ children, workspace }: PropsWithChildren<Props>) => {
  const mainContainerRef = useRef<HTMLDivElement>(null)
  const isSystemRedesignEnabled = useIsSystemRedesign()
  const [showUnregisteredUserBanner] = useUnregisteredUserBanner()
  const { superAdminMode } = useSuperAdmin()
  const dispatch = useDispatch()
  const location = useLocation()
  const { leftSidebarStore, workspaceTheme, filesViewSchemas } = useRootStore()
  const sidebarState = useSidebarState()
  const floatingSidebar = useSelector(({ floatingSidebar }: RootState) => floatingSidebar, isEqual)
  const handbook = useHandbook()
  const folderRouteMatch = useRouteMatch(FOLDER_PATH)
  const isCalendarView = filesViewSchemas.getCurrentSchema()?.type === ViewType.Calendar && folderRouteMatch
  const { setShowFloatingOrgSidebar } = useShowFloatingOrgSidebar()
  const layout = useLayout()
  const blazeHomePath = useRouteMatch(BLAZE_HOME_PATH)
  const recentDocsPath = useRouteMatch(RECENT_DOCS_PATH)
  const isAPMobileWeb = useAPMobileWeb()
  const isOnboardingStrategiesEnabled = useOnboardingStrategiesEnabled()
  const { data: brandPlan } = useCurrentBrandPlan()

  // NOTE ON BANNERS: If you add a new banner, make sure to update the offset calculation in the Header component (src/screens/blaze/Home/VirtualMarketerHome/Brain/PlatformSection/Header.tsx).
  const { annualTrialDiscountBanner, showAnnualTrialDiscountBanner } = useAnnualTrialDiscountBanner()
  const { showBanner: showAutopilotPausedBanner, autopilotPausedBanner } = useAutopilotPausedBanner()
  const { showBanner: showTrialEndedBanner, trialEndedBanner } = useTrialEndedBanner()
  const { showBanner: showAutopilotUpgradeBanner, autopilotUpgradeBanner } = useAutopilotUpgradeBanner()
  const {
    showBanner: showIntegrationMaintenanceBanner,
    integrationMaintenanceBanner,
    totalHeight: integrationMaintenanceBannerHeight,
  } = useIntegrationMaintenanceBanner()
  const { showBanner: showFreePlanBanner, freePlanBanner } = useFreePlanBanner()

  useEffect(() => {
    if (layout.dashboardContent) {
      layout.dashboardContent.scrollTop = 0
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname])

  useLongHover(
    mainContainerRef,
    () => {
      if (floatingSidebar) {
        setShowFloatingOrgSidebar(false)
        dispatch(setFloatingSidebar(false))
      }
    },
    () => null,
    1,
  )

  if (isAPMobileWeb && isOnboardingStrategiesEnabled && brandPlan?.has_content_strategies) {
    return <MobileStopForStrategies />
  }

  return (
    <div
      className={cx(Styles.dashboard, {
        [Styles.withAutopilotPausedBanner]: showAutopilotPausedBanner,
        [Styles.withTrialEndedBanner]: showTrialEndedBanner,
        [Styles.withAutopilotUpgradeBanner]: showAutopilotUpgradeBanner,
        [Styles.withAnnualTrialDiscountBanner]: showAnnualTrialDiscountBanner,
        [Styles.withIntegrationMaintenanceBanner]: showIntegrationMaintenanceBanner,
        [Styles.withUnregisteredBanner]: showUnregisteredUserBanner,
        [Styles.withFreePlanBanner]: showFreePlanBanner,
        [Styles.inSuperAdminMode]: superAdminMode,
        [Styles.isCalendarView]: isCalendarView,
      })}
      id="workspace-dashboard"
      style={{
        ...workspaceTheme.paletteProperties(['--sidebar-background-color']),
        ...(showIntegrationMaintenanceBanner && {
          '--integration-maintenance-banner-height': `${integrationMaintenanceBannerHeight}px`,
        }),
      }}
    >
      {showIntegrationMaintenanceBanner && integrationMaintenanceBanner}
      {showAutopilotPausedBanner && !showTrialEndedBanner && autopilotPausedBanner}
      {showTrialEndedBanner && trialEndedBanner}
      {showAnnualTrialDiscountBanner && annualTrialDiscountBanner}
      {showAutopilotUpgradeBanner && autopilotUpgradeBanner}
      {showFreePlanBanner && freePlanBanner}
      {workspace?.paying && workspace?.payment_failed && <UpdateBlazePaymentMethodModal />}
      {sidebarState.isVisible && <WorkspaceDashboardSidebar />}
      <div className={Styles.innerContent}>
        {!blazeHomePath && !recentDocsPath && !isSystemRedesignEnabled && <NewTopBar isLockable={false} />}
        <HandbookProvider handbookKey={handbook?.key}>
          <Container className={cx(Styles.mainContainer, Styles.notCore, Styles.notEditing)} ref={mainContainerRef}>
            <div
              className={Styles.contentWrapper}
              id="dashboard-content-wrapper"
              ref={(node) => layout.setDashboardContent(node)}
              onMouseEnter={() => leftSidebarStore.setIsHovered(false)}
            >
              {children}
              <SettingsContainer workspace={workspace} />
            </div>
          </Container>
        </HandbookProvider>
        <GenerationStatusNotification />
      </div>
      <div id="workspace-dashboard-right-column-portal" />
      <ContentGenerationWatcher />
    </div>
  )
})
